import { Info } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { ChecklistItem, type ChecklistItemProps } from './ChecklistItem'

/**
 * Quickcheck — the "Before you begin" prerequisites panel at the top
 * of guide and tutorial topics.
 *
 * Design plan reference,
 * section 21.4 (every guide / tutorial topic starts with a
 * quickcheck) + section 22.3 (article page mockup shows the panel
 * directly under the H1).
 *
 * Visual: tinted info panel with a header line and a stack of
 * ChecklistItem rows. Each row carries a status (done / pending /
 * warning / error) — typically the docs site emits pending rows
 * with prose like "An active tenant", "Role with `applications.write`
 * scope", etc., letting the reader self-verify before they spend ten
 * minutes following the wrong guide.
 *
 * Default status across rows is `pending`; callers can override
 * per-row when one of the checks has been performed dynamically
 * (e.g. an in-browser SDK probe).
 */

export interface QuickcheckItem {
  label: React.ReactNode
  description?: React.ReactNode
  /** Defaults to 'pending'. */
  status?: ChecklistItemProps['status']
}

export interface QuickcheckProps extends React.HTMLAttributes<HTMLElement> {
  items: QuickcheckItem[]
  /** Override the header label. Defaults to 'Before you begin'. */
  label?: string
  /** Optional one-line subtitle below the header. */
  description?: React.ReactNode
}

export const Quickcheck = React.forwardRef<HTMLElement, QuickcheckProps>(
  (
    { items, label = 'Before you begin', description, className, ...rest },
    ref,
  ) => {
    if (items.length === 0) return null

    return (
      <section
        ref={ref}
        aria-label={label}
        className={cn(
          'rounded-lg border border-ia-cyan/30 bg-ia-cyan-muted/40 p-4',
          className,
        )}
        {...rest}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex items-center justify-center h-6 w-6 rounded-pill bg-ia-cyan-muted text-ia-cyan-text shrink-0">
            <Info size={14} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-body-sm font-semibold text-ia-text">
              {label}
            </div>
            {description && (
              <div className="mt-0.5 text-body-sm text-ia-text-secondary">
                {description}
              </div>
            )}
            <div className="mt-2">
              {items.map((item, i) => (
                <ChecklistItem
                  key={i}
                  status={item.status ?? 'pending'}
                  label={item.label}
                  description={item.description}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    )
  },
)
Quickcheck.displayName = 'Quickcheck'
